import { Avatar, Box, Flex, Text, TextField } from '@radix-ui/themes';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonHitterAvatar } from 'components/common/hitters/avatar';
import { getHitterOption } from 'components/common/hitters/hitter-option';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { CrudAction } from 'enums/tables';
import { t } from 'i18next';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { PLAYER_LEVELS_DICT } from 'lib_ts/enums/pitches.enums';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { first } from 'lodash';
import { ReactNode, useCallback, useMemo } from 'react';
import useHittersStore from 'stores/use-hitters-store';

const CREATE_HITTER_ID = '----create----';
const LIST_MAX_WIDTH = '300px';
const MAX_NUM_RECENTLY_SELECTED = 10;
const RECENTLY_SELECTED_GROUP_LABEL = t('common.recently-selected').toString();

export const SearchHitter = (props: {
  // PL sidebar uses all hitters, Analytics uses filtered hitters
  hitters: IHitter[];
}) => {
  const lastChanged = useHittersStore((s) => s.lastChanged);

  const activeHitter = useHittersStore((s) => s.activeHitter);
  // const filtered = useHittersStore((s) => s.filtered);
  // const hitters = useHittersStore((s) => s.hitters);

  const filteredOptions = useMemo(() => {
    const recentlySelected = props.hitters
      .sort((a, b) => {
        if (a.lastActive && b.lastActive) {
          return -StringHelper.safeSort(a.lastActive, b.lastActive);
        }

        if (a.lastActive) {
          return -1;
        }

        return 1;
      })
      .slice(0, MAX_NUM_RECENTLY_SELECTED)
      .filter((h) => !!h.lastActive) // we might have fewer than MAX_NUM_RECENTLY_SELECTED with a lastActive
      .map((h) => getHitterOption(h, RECENTLY_SELECTED_GROUP_LABEL));

    const nonRecentlySelectedOptions = props.hitters
      .filter((h) => !recentlySelected.find((r) => r.value === h._id))
      .map((h) => {
        const levelLabel = PLAYER_LEVELS_DICT[h.level]?.label;
        return getHitterOption(
          h,
          levelLabel ? `${t('common.level')}: ${t(levelLabel)}` : 'No Level'
        );
      })
      .sort((a, b) => StringHelper.safeSort(a.group, b.group));

    return [...recentlySelected, ...nonRecentlySelectedOptions];
  }, [props.hitters]);

  const renderTrigger = useCallback(
    (value: string[], slotIcon: ReactNode) => {
      const hitterID = first(value);

      if (!hitterID) {
        return (
          <Box position="relative" className="cursor-pointer">
            <TextField.Root
              style={{
                pointerEvents: 'none',
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: -1,
              }}
              variant="surface"
              onChange={() => {}}
            />
            <Flex align="center" gap="2" p="2">
              <Avatar
                fallback="N/A"
                size="2"
                // background color
                color="gray"
                style={{
                  // font color
                  color: 'var(--gray-a11)',
                }}
              />
              <Text truncate>{t('hitters.select-hitter')}</Text>
              <Flex
                align="center"
                ml="auto"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {slotIcon}
              </Flex>
            </Flex>
          </Box>
        );
      }

      const hitter = props.hitters.find((o) => o._id === hitterID);

      if (!hitter) {
        return;
      }

      // e.g. J. Smith, V. Guerrero Jr.
      const displayName = StringHelper.shortenName(hitter.name);

      return (
        <Box position="relative" className="cursor-pointer">
          <TextField.Root
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              width: '100%',
              height: '100%',
              zIndex: -1,
            }}
            variant="surface"
            onChange={() => {}}
          />
          <Flex align="center" gap="2" p="2">
            <CommonHitterAvatar size="2" hitter={hitter} clickDisabled />
            <Text truncate mr="auto">
              {displayName}
            </Text>
            <HitterSideBadge value={hitter.side} />
            <Flex
              align="center"
              onClick={(e) => {
                e.stopPropagation();
                useHittersStore.setState({ activeHitter: undefined });
              }}
            >
              {slotIcon}
            </Flex>
          </Flex>
        </Box>
      );
    },
    [activeHitter, props.hitters]
  );

  return (
    <CommonSearchInput
      key={lastChanged}
      id="hitters-hitter"
      placeholder="common.hitter"
      options={[
        {
          label: t('hitters.add-a-new-hitter'),
          value: CREATE_HITTER_ID,
        },
        ...filteredOptions,
      ]}
      values={activeHitter ? [activeHitter._id] : []}
      onChange={(v) => {
        if (v.length === 0) {
          useHittersStore.setState({ activeHitter: undefined });
          return;
        }

        const hitterID = v[0];

        if (!hitterID) {
          return;
        }

        if (hitterID === CREATE_HITTER_ID) {
          useHittersStore.setState({
            dialogCRUD: {
              key: Date.now(),
              action: CrudAction.Create,
              models: [],
            },
          });
          return;
        }

        useHittersStore.getState().activate(hitterID);
      }}
      renderTrigger={renderTrigger}
      contentMaxWidth={LIST_MAX_WIDTH}
      strict
      matchDigits
      skipSort
      optional
    />
  );
};
